import React from 'react';
import { Box, Typography } from '@mui/material';
import SavingsIcon from '@mui/icons-material/Savings';

const COLOR_RED = '#d03b3b';
const COLOR_AMBER = '#e0a11a';
const COLOR_BLUE = '#2a78d6';
const COLOR_GREEN = '#0ca30c';
const COLOR_PURPLE = '#7e4fd1';
const NAVY = '#0d2a5c';
const BORDER = '#ECEFF3';
const TEXT_PRIMARY = '#0b0b0b';
const TEXT_MUTED = '#898781';

const fmtCost = (value) => `₹${Math.round(value || 0).toLocaleString('en-IN')}`;
const fmtHrs = (totalSeconds) => `${((totalSeconds || 0) / 3600).toFixed(1)} hrs`;

const secondsToHms = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '—';
  const abs = Math.abs(Math.round(totalSeconds));
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const seconds = abs % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const Card = ({ children, sx }) => (
  <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, background: '#ffffff', p: 3, ...sx }}>{children}</Box>
);

const FinancialImpactBreakdown = ({ summary }) => (
  <Card>
    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: TEXT_PRIMARY, mb: 2 }}>
      FINANCIAL IMPACT BREAKDOWN
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, flex: '1 1 220px',
          background: '#eefaf0', borderRadius: 2, p: 1.5,
        }}
      >
        <SavingsIcon sx={{ color: COLOR_GREEN, fontSize: 26 }} />
        <Box>
          <Typography sx={{ fontSize: '12px', color: TEXT_MUTED }}>Production Loss</Typography>
          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: COLOR_GREEN }}>{fmtCost(summary.extra_part_cost)}</Typography>
        </Box>
      </Box>
    </Box>
  </Card>
);

const OeeBarRow = ({ label, actual, potential }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: TEXT_PRIMARY, mb: 0.5 }}>{label}</Typography>
    {[
      { value: actual, color: NAVY },
      { value: potential, color: COLOR_GREEN },
    ].map((bar, i) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ flex: 1, height: 14, background: '#f0f0ee', borderRadius: 1, overflow: 'hidden' }}>
          <Box sx={{ width: `${Math.min(100, bar.value)}%`, height: '100%', background: bar.color, borderRadius: 1 }} />
        </Box>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: bar.color, width: 48, textAlign: 'right' }}>
          {bar.value}%
        </Typography>
      </Box>
    ))}
  </Box>
);

const OeeActualVsPotential = ({ oee }) => (
  <Card>
    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: TEXT_PRIMARY, mb: 0.5 }}>
      OEE — ACTUAL VS POTENTIAL
    </Typography>
    <Typography sx={{ fontSize: '11px', color: TEXT_MUTED, mb: 1.5 }}>
      Potential = if extra idle time and below-target cycle speed were recovered
    </Typography>
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: NAVY }} />
        <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Actual</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: COLOR_GREEN }} />
        <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>Potential</Typography>
      </Box>
    </Box>

    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      <Box
        sx={{
          flex: '1 1 220px', background: '#f5f7fb', borderRadius: 2, p: 2,
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: TEXT_MUTED }}>OEE IMPROVEMENT OPPORTUNITY</Typography>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: TEXT_PRIMARY, mt: 0.5 }}>
          {oee.actual.oee}% → {oee.potential.oee}%
        </Typography>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLOR_GREEN, mt: 0.5 }}>
          +{oee.oee_improvement_points} percentage points
        </Typography>
      </Box>
      <Box sx={{ flex: '2 1 320px' }}>
        <OeeBarRow label="Availability" actual={oee.actual.availability} potential={oee.potential.availability} />
        <OeeBarRow label="Performance" actual={oee.actual.performance} potential={oee.potential.performance} />
        <OeeBarRow label="OEE" actual={oee.actual.oee} potential={oee.potential.oee} />
      </Box>
    </Box>
  </Card>
);

const ACTION_TEXT = {
  total_idle_hours: 'Review and reduce idle time. Optimize workflow and operator efficiency.',
  top_idle_reason: 'Address the top idle reason directly - it is the single biggest recoverable time block.',
  top_alarm: 'Investigate root cause of the top alarm. Implement preventive maintenance.',
  cycle_speed_extremes: 'Monitor cycle speed regularly to avoid quality risks.',
  top_idle_component: 'Focus idle-reduction efforts on this component/machine first - it is driving the most lost potential.',
  unallocated_machine_time: 'Assign route cards/components to unallocated sessions so idle time gets attributed correctly.',
  // old items, added back alongside the new ones (not a replacement).
  excess_idle_time: 'Review and reduce idle time. Optimize workflow and operator efficiency.',
  below_target_cycle_speed: 'Review and optimize cycle parameters. Ensure consistent operation at target speed.',
  alarms_impacting_availability: 'Investigate root cause of the top alarm. Implement preventive maintenance.',
};

// MANAGEMENT ACTIONS - exactly 4 points: total_idle_hours, top_idle_reason,
// top_alarm, cycle_speed_extremes. (top_idle_component/unallocated_machine_time
// and the 3 old items are not shown here.)
const buildActionRows = (items) => {
  const byKey = Object.fromEntries((items || []).map((i) => [i.key, i]));
  const totalIdle = byKey.total_idle_hours;
  const topReason = byKey.top_idle_reason;
  const topAlarm = byKey.top_alarm;
  const extremes = byKey.cycle_speed_extremes;

  return [
    totalIdle && {
      color: COLOR_RED,
      issue: 'Total Idle Hours',
      impactLine1: fmtHrs(totalIdle.idle_time),
      impactLine2: 'Idle Time',
      action: ACTION_TEXT.total_idle_hours,
    },
    topReason && topReason.name && {
      color: COLOR_RED,
      issue: `Top Idle Reason: ${topReason.name}`,
      impactLine1: secondsToHms(topReason.total_idle_duration),
      impactLine2: `(${topReason.count} occurrences)`,
      action: ACTION_TEXT.top_idle_reason,
    },
    topAlarm && topAlarm.alarm_message && {
      color: COLOR_AMBER,
      issue: `Top Alarm: ${topAlarm.alarm_message.trim()}`,
      impactLine1: secondsToHms(topAlarm.total_alarm_duration),
      impactLine2: `(${topAlarm.count} occurrences)`,
      action: ACTION_TEXT.top_alarm,
    },
    extremes && {
      color: COLOR_BLUE,
      issue: 'Out-of-Range Cycles',
      impactLine1: `${extremes.total_out_of_range} Cycles`,
      impactLine2: `(${extremes.low_cycle} low / ${extremes.high_cycle} high)`,
      action: ACTION_TEXT.cycle_speed_extremes,
    },
  ].filter(Boolean);
};

const ManagementActions = ({ attentionItems }) => {
  const rows = buildActionRows(attentionItems);
  return (
    <Card sx={{ p: 0, overflow: 'hidden' }}>
      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: TEXT_PRIMARY, p: 3, pb: 2 }}>
        MANAGEMENT ACTIONS
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <Box component="thead">
            <Box component="tr" sx={{ background: NAVY }}>
              {['Priority', 'Issue', 'Impact / Opportunity', 'Recommended Action'].map((h) => (
                <Box
                  component="th"
                  key={h}
                  sx={{ color: '#fff', fontSize: '11px', fontWeight: 700, textAlign: 'left', p: 1.5, letterSpacing: '0.02em' }}
                >
                  {h}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((row, idx) => (
              <Box component="tr" key={row.issue} sx={{ borderBottom: `1px solid ${BORDER}` }}>
                <Box component="td" sx={{ p: 1.5, verticalAlign: 'top' }}>
                  <Box
                    sx={{
                      width: 24, height: 24, borderRadius: '50%', background: row.color, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700,
                    }}
                  >
                    {idx + 1}
                  </Box>
                </Box>
                <Box component="td" sx={{ p: 1.5, fontSize: '13px', color: TEXT_PRIMARY, verticalAlign: 'top' }}>{row.issue}</Box>
                <Box component="td" sx={{ p: 1.5, verticalAlign: 'top' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: row.color }}>{row.impactLine1}</Typography>
                  <Typography sx={{ fontSize: '11px', color: TEXT_MUTED }}>{row.impactLine2}</Typography>
                </Box>
                <Box component="td" sx={{ p: 1.5, fontSize: '12px', color: TEXT_PRIMARY, verticalAlign: 'top' }}>{row.action}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

const ClosingBanner = ({ summary, oee }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${NAVY} 0%, #123a7a 100%)`,
      borderRadius: 2, color: '#fff', p: 2.5, textAlign: 'center',
    }}
  >
    <Typography sx={{ fontSize: '14px' }}>
      Focus on these actions to unlock{' '}
      <Box component="span" sx={{ color: '#f2c94c', fontWeight: 700 }}>{fmtCost(summary.extra_part_cost)}</Box>
      {' '}in value and improve OEE to{' '}
      <Box component="span" sx={{ color: '#8fe08f', fontWeight: 700 }}>{oee.potential.oee}%</Box>
    </Typography>
  </Box>
);

// Page 2 of the Insights4 Overview - Financial Impact Breakdown, OEE Actual vs
// Potential bars, Management Actions table, closing banner. Fed by the same
// overview-summary / management-attention-list data as page 1, plus the new
// oee-actual-vs-potential endpoint.
export default function OverviewPage2({ summary, attentionItems, oee }) {
  if (!summary || !oee) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <FinancialImpactBreakdown summary={summary} />
      <OeeActualVsPotential oee={oee} />
      <ManagementActions attentionItems={attentionItems} />
      <ClosingBanner summary={summary} oee={oee} />
    </Box>
  );
}
